import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import Avatar from '../../../base/avatar/components/Avatar';
import { getParticipantDisplayName } from '../../../base/participants/functions';
import Dialog from '../../../base/ui/components/web/Dialog';
import Select from '../../../base/ui/components/web/Select';
import { declineUnavailable, deny, grant, setChosenDisplay } from '../../actions';
import { findSharedDisplay, isWholeScreenShared } from '../../functions';
import { AgentStatus } from '../../types';
import AgentSetup from './AgentSetup';
const useStyles = makeStyles()(theme => {
    return {
        person: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(3),
            paddingBottom: theme.spacing(3)
        },
        name: {
            ...theme.typography.bodyShortBoldLarge,
            color: theme.palette.text01
        },
        body: {
            ...theme.typography.bodyLongRegular,
            color: theme.palette.text01,
            maxWidth: '52ch'
        },
        abilities: {
            margin: `${theme.spacing(3)} 0`,
            padding: 0,
            listStyle: 'none',
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(2)
        },
        ability: {
            ...theme.typography.bodyShortRegular,
            color: theme.palette.text01,
            display: 'flex',
            alignItems: 'baseline',
            gap: theme.spacing(2),
            '&::before': {
                content: '""',
                flex: '0 0 auto',
                width: '5px',
                height: '5px',
                borderRadius: '50%',
                background: theme.palette.action01
            }
        },
        picker: {
            marginTop: theme.spacing(3)
        },
        note: {
            ...theme.typography.labelRegular,
            color: theme.palette.text02,
            marginTop: theme.spacing(3),
            maxWidth: '52ch'
        }
    };
});
/**
 * Asks whether someone else may drive this computer.
 *
 * The question is answered once per session and can be withdrawn at any moment
 * afterwards, so the dialog's job is to be specific about what is being agreed
 * to rather than to warn. It says exactly what the other person will be able to
 * do, and where the way out is.
 *
 * @param {IProps} props - The properties.
 * @returns {ReactElement}
 */
const RemoteControlAuthorizationDialog = ({ participantId }) => {
    const { classes } = useStyles();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const displayName = useSelector((state) => getParticipantDisplayName(state, participantId));
    const agentStatus = useSelector((state) => state['features/remote-control'].agent.status);
    const displays = useSelector((state) => state['features/remote-control'].agent.hello?.displays ?? []);
    const sharingWholeScreen = useSelector(isWholeScreenShared);
    const suggestedDisplay = useSelector(findSharedDisplay);
    const [chosen, setChosen] = useState(suggestedDisplay?.id ?? '');
    const options = useMemo(() => displays.map(display => ({
        label: display.primary
            ? t('remoteControl.displayPrimary', {
                name: display.name,
                width: display.width,
                height: display.height
            })
            : t('remoteControl.display', {
                name: display.name,
                width: display.width,
                height: display.height
            }),
        value: display.id
    })), [displays, t]);
    const onChooseDisplay = useCallback((event) => {
        setChosen(event.target.value);
        dispatch(setChosenDisplay(event.target.value));
    }, [dispatch]);
    const onCancel = useCallback(() => {
        dispatch(deny(participantId));
        return true;
    }, [dispatch, participantId]);
    const onUnavailable = useCallback(() => {
        dispatch(declineUnavailable(participantId));
        return true;
    }, [dispatch, participantId]);
    const onSubmit = useCallback(() => {
        dispatch(grant(participantId));
        // Closed by hand once the screen picker, if the browser needs to show
        // one, has been dealt with.
        return false;
    }, [dispatch, participantId]);
    // The agent can go away between advertising that this machine can be
    // controlled and somebody asking. Saying so beats a request that fails for
    // no visible reason.
    if (agentStatus !== AgentStatus.Available) {
        return (React.createElement(Dialog, { cancel: { hidden: true }, ok: { translationKey: 'dialog.close' }, onCancel: onUnavailable, onSubmit: onUnavailable, titleKey: 'remoteControl.setupTitle' },
            React.createElement(AgentSetup, null)));
    }
    return (React.createElement(Dialog, { cancel: { translationKey: 'remoteControl.dontAllow' }, ok: { translationKey: 'remoteControl.allow' }, onCancel: onCancel, onSubmit: onSubmit, title: t('remoteControl.requestTitle', { user: displayName }) },
        React.createElement("div", { className: classes.person },
            React.createElement(Avatar, { participantId: participantId, size: 40 }),
            React.createElement("span", { className: classes.name }, displayName)),
        React.createElement("p", { className: classes.body }, t('remoteControl.requestBody')),
        React.createElement("ul", { className: classes.abilities },
            React.createElement("li", { className: classes.ability }, t('remoteControl.abilityPointer')),
            React.createElement("li", { className: classes.ability }, t('remoteControl.abilityClick')),
            React.createElement("li", { className: classes.ability }, t('remoteControl.abilityType'))),
        !sharingWholeScreen && (React.createElement("p", { className: classes.body }, t('remoteControl.willShareScreen'))),
        displays.length > 1 && (React.createElement("div", { className: classes.picker },
            React.createElement(Select, { id: 'remote-control-display', label: t('remoteControl.whichScreen'), onChange: onChooseDisplay, options: options, value: chosen }))),
        React.createElement("p", { className: classes.note }, t('remoteControl.stopAnyTime'))));
};
export default RemoteControlAuthorizationDialog;
